// JobClose AI · Mohit QA Reporting V1 · Contractor-side surface.
// Three tabs: New Finding · My Findings · Retests Assigned To Me.
// Backed by /api/qa/*. Ordinary contractor role preserved — the tab is
// gated by capability, not by grant of admin privileges.
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import {
  Bug, ListChecks, RefreshCw, Loader2, Paperclip, Send,
  CheckCircle2, AlertTriangle, XCircle, MinusCircle,
} from "lucide-react";

const RESULTS = ["PASS", "PARTIAL", "FAIL", "NOT_TESTABLE"];
const CLASSIFICATIONS = ["DEFECT", "USABILITY", "IMPROVEMENT"];
const SEVERITIES = ["P0", "P1", "P2", "P3"];

const RESULT_ICON = {
  PASS: <CheckCircle2 size={13} className="text-emerald-600" />,
  PARTIAL: <AlertTriangle size={13} className="text-amber-600" />,
  FAIL: <XCircle size={13} className="text-red-600" />,
  NOT_TESTABLE: <MinusCircle size={13} className="text-slate-500" />,
};

const emptyForm = {
  test_id: "", area: "", tenant_label: "",
  device: "", browser: "", ui_language: "en",
  environment: "preview", build_ref: "",
  starting_state: "", exact_steps: "", exact_input: "",
  expected_result: "", actual_result: "",
  result: "FAIL",
  classification: "DEFECT", severity: "P2",
  session_id: "", repro_count: 1, known_issue: false,
};

function StatusBadge({ status }) {
  const tones = {
    new: "bg-slate-100 text-slate-800",
    in_progress: "bg-blue-100 text-blue-800",
    ready_for_retest: "bg-amber-100 text-amber-800",
    deferred: "bg-slate-100 text-slate-600",
    closed_fixed: "bg-emerald-100 text-emerald-800",
    closed_wont_fix: "bg-slate-200 text-slate-700",
    closed_not_a_bug: "bg-slate-200 text-slate-700",
  };
  return (
    <span
      data-testid="qa-status-badge"
      className={`text-[11px] px-1.5 py-0.5 rounded ${tones[status] || tones.new}`}
    >
      {status}
    </span>
  );
}

function NewFindingTab({ onSubmitted }) {
  const [form, setForm] = useState(emptyForm);
  const [attachments, setAttachments] = useState([]); // [{id, name, kind}]
  const [saving, setSaving] = useState(false);

  const setF = (k) => (e) =>
    setForm((p) => ({
      ...p, [k]: e?.target?.type === "checkbox" ? e.target.checked
        : (e?.target?.value ?? e),
    }));

  const requireCS = form.result === "PARTIAL" || form.result === "FAIL";

  const submitFinding = async () => {
    if (!form.exact_steps.trim() || !form.expected_result.trim()) {
      toast.error("Exact steps + expected result are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        evidence_attachment_ids: attachments.map((a) => a.id),
        classification: requireCS ? form.classification : null,
        severity: requireCS ? form.severity : null,
      };
      const { data } = await api.post("/qa/findings", payload);
      toast.success(`Finding recorded — id ${data.finding.id.slice(0, 8)}`);
      setForm(emptyForm);
      setAttachments([]);
      onSubmitted?.(data.finding);
    } catch (err) {
      const errs = err?.response?.data?.detail?.field_errors;
      if (errs) {
        toast.error(Object.values(errs)[0] || "Validation error");
      } else {
        toast.error("Save failed. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  // Attachments upload against a temporary finding — V1 shortcut:
  // upload after a draft-save. Here we upload against the LAST created
  // finding via a small pre-save trick: allow attachment upload only
  // AFTER the first save. In V1 the contractor can also add evidence
  // by first saving and then attaching from the detail drawer.
  return (
    <div className="space-y-4" data-testid="qa-newfinding-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Test ID" testId="qa-f-test_id">
          <input className="jc-input" value={form.test_id} onChange={setF("test_id")} />
        </Field>
        <Field label="Area / workflow" testId="qa-f-area">
          <input className="jc-input" value={form.area} onChange={setF("area")}
                 placeholder="e.g. Business Texting · Setup" />
        </Field>
        <Field label="Tenant label" testId="qa-f-tenant_label">
          <input className="jc-input" value={form.tenant_label}
                 onChange={setF("tenant_label")}
                 placeholder="e.g. mohit-clean-1" />
        </Field>
        <Field label="Environment" testId="qa-f-environment">
          <select className="jc-input" value={form.environment}
                  onChange={setF("environment")}>
            <option value="preview">preview</option>
            <option value="production">production</option>
            <option value="local">local</option>
          </select>
        </Field>
        <Field label="Build reference" testId="qa-f-build_ref">
          <input className="jc-input" value={form.build_ref}
                 onChange={setF("build_ref")}
                 placeholder="e.g. publish-33 · 122d773" />
        </Field>
        <Field label="Device" testId="qa-f-device">
          <input className="jc-input" value={form.device}
                 onChange={setF("device")} placeholder="e.g. Pixel 8 · Android 14" />
        </Field>
        <Field label="Browser" testId="qa-f-browser">
          <input className="jc-input" value={form.browser}
                 onChange={setF("browser")} placeholder="e.g. Chrome 125" />
        </Field>
        <Field label="UI language" testId="qa-f-lang">
          <input className="jc-input" value={form.ui_language}
                 onChange={setF("ui_language")} placeholder="en / es" />
        </Field>
      </div>

      <TextArea label="Starting state" value={form.starting_state}
                onChange={setF("starting_state")} testId="qa-f-starting" />
      <TextArea label="Exact steps (required)" value={form.exact_steps}
                onChange={setF("exact_steps")} testId="qa-f-steps" rows={4} />
      <TextArea label="Exact input" value={form.exact_input}
                onChange={setF("exact_input")} testId="qa-f-input" />
      <TextArea label="Expected result (required)" value={form.expected_result}
                onChange={setF("expected_result")} testId="qa-f-expected" />
      <TextArea label="Actual result" value={form.actual_result}
                onChange={setF("actual_result")} testId="qa-f-actual" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Field label="Result" testId="qa-f-result">
          <select className="jc-input" value={form.result}
                  onChange={setF("result")}>
            {RESULTS.map((r) => (<option key={r} value={r}>{r}</option>))}
          </select>
        </Field>
        <Field label="Classification" testId="qa-f-classification">
          <select className="jc-input" value={form.classification || ""}
                  onChange={setF("classification")} disabled={!requireCS}>
            {CLASSIFICATIONS.map((c) => (<option key={c} value={c}>{c}</option>))}
          </select>
        </Field>
        <Field label="Severity" testId="qa-f-severity">
          <select className="jc-input" value={form.severity || ""}
                  onChange={setF("severity")} disabled={!requireCS}>
            {SEVERITIES.map((s) => (<option key={s} value={s}>{s}</option>))}
          </select>
        </Field>
        <Field label="Repro count" testId="qa-f-repro">
          <input type="number" min={1} max={999} className="jc-input"
                 value={form.repro_count}
                 onChange={setF("repro_count")} />
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Session ID (optional)" testId="qa-f-session">
          <input className="jc-input" value={form.session_id}
                 onChange={setF("session_id")}
                 placeholder="Levi command session id" />
        </Field>
        <label className="flex items-center gap-2 text-sm mt-6"
               data-testid="qa-f-known_issue">
          <input type="checkbox" checked={form.known_issue}
                 onChange={setF("known_issue")} />
          <span>This looks like a known / previously reported issue</span>
        </label>
      </div>

      <div className="flex items-center justify-end gap-2 pt-2 border-t border-[var(--jc-border)]">
        <button
          type="button"
          disabled={saving}
          onClick={submitFinding}
          data-testid="qa-submit-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-semibold jc-btn-blue disabled:opacity-50"
        >
          {saving && <Loader2 className="animate-spin" size={14} />}
          <Send size={14} /> Save finding
        </button>
      </div>
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block space-y-0.5" data-testid={testId}>
      <span className="text-xs font-semibold text-[var(--jc-text-soft)]">
        {label}
      </span>
      {children}
    </label>
  );
}

function TextArea({ label, value, onChange, rows = 3, testId }) {
  return (
    <label className="block space-y-0.5" data-testid={testId}>
      <span className="text-xs font-semibold text-[var(--jc-text-soft)]">
        {label}
      </span>
      <textarea className="jc-input" rows={rows}
                value={value} onChange={onChange} />
    </label>
  );
}

function FindingsList({ filter, refreshKey, onOpen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter?.ready_for_retest) params.append("ready_for_retest", "true");
      const { data } = await api.get(`/qa/findings?${params.toString()}`);
      setRows(data.findings || []);
    } catch (e) {
      toast.error("Couldn't load findings.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) return (
    <div className="p-6 text-sm text-[var(--jc-text-soft)] flex items-center gap-2">
      <Loader2 className="animate-spin" size={14} /> Loading…
    </div>
  );
  if (!rows.length) return (
    <div className="p-8 text-sm text-[var(--jc-text-soft)] text-center border border-dashed rounded"
         data-testid="qa-empty">
      No findings yet.
    </div>
  );

  return (
    <div className="jc-card overflow-x-auto" data-testid="qa-findings-list">
      <table className="w-full text-sm">
        <thead className="bg-[var(--jc-bg)] text-left text-[var(--jc-text-soft)] text-xs">
          <tr>
            <th className="px-3 py-2">Result</th>
            <th className="px-3 py-2">Test ID / Area</th>
            <th className="px-3 py-2">Tenant</th>
            <th className="px-3 py-2">Severity</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Updated</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => (
            <tr key={f.id} data-testid={`qa-row-${f.id}`}
                className="border-t border-[var(--jc-border)] hover:bg-[var(--jc-bg)] cursor-pointer"
                onClick={() => onOpen?.(f)}>
              <td className="px-3 py-2">
                <span className="inline-flex items-center gap-1">
                  {RESULT_ICON[f.result]} {f.result}
                </span>
              </td>
              <td className="px-3 py-2">
                <div className="font-semibold">{f.test_id || "—"}</div>
                <div className="text-xs text-[var(--jc-text-soft)]">
                  {f.area || "(no area)"}
                </div>
              </td>
              <td className="px-3 py-2 text-xs">{f.tenant_label || "—"}</td>
              <td className="px-3 py-2 text-xs">{f.severity || "—"}</td>
              <td className="px-3 py-2"><StatusBadge status={f.engineering_status} /></td>
              <td className="px-3 py-2 text-xs">{f.updated_at?.slice(0, 10)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FindingDrawer({ finding, onClose, onChange }) {
  const [detail, setDetail] = useState(null);
  const [retestNotes, setRetestNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState([]);

  const load = useCallback(async () => {
    if (!finding?.id) return;
    const { data } = await api.get(`/qa/findings/${finding.id}`);
    setDetail(data);
  }, [finding?.id]);
  useEffect(() => { load(); }, [load]);

  const uploadEvidence = async (file) => {
    if (!file) return;
    setSaving(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(
        `/qa/findings/${finding.id}/evidence`, form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setAttachments((a) => [...a, {
        id: data.attachment_id, name: file.name, kind: data.kind,
      }]);
      toast.success("Evidence attached");
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(
        (d?.error === "unsupported_media_type" && "Unsupported file type")
        || (d?.error === "payload_too_large" && "File too large")
        || (d?.error === "too_many_attachments" && "Attachment limit reached")
        || "Upload failed",
      );
    } finally { setSaving(false); }
  };

  const submitRetest = async (result) => {
    setSaving(true);
    try {
      await api.post(`/qa/findings/${finding.id}/retest`, {
        result, notes: retestNotes,
        evidence_attachment_ids: attachments.map((a) => a.id),
      });
      toast.success(`Retest ${result} recorded`);
      onChange?.();
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "not_ready_for_retest") {
        toast.error("This finding is not currently ready for retest.");
      } else {
        toast.error("Retest failed.");
      }
    } finally { setSaving(false); }
  };

  if (!finding) return null;
  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex justify-end"
         data-testid="qa-finding-drawer" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white h-full overflow-y-auto p-6 space-y-4"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          {RESULT_ICON[finding.result]}
          <h2 className="text-lg font-semibold flex-1"
              data-testid="qa-drawer-title">
            {finding.test_id || "Finding"} · {finding.result}
          </h2>
          <StatusBadge status={finding.engineering_status} />
          <button onClick={onClose} className="text-sm">Close</button>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-[var(--jc-text-soft)]">Area:</span> {finding.area || "—"}</div>
          <div><span className="text-[var(--jc-text-soft)]">Tenant:</span> {finding.tenant_label || "—"}</div>
          <div><span className="text-[var(--jc-text-soft)]">Severity:</span> {finding.severity || "—"}</div>
          <div><span className="text-[var(--jc-text-soft)]">Classification:</span> {finding.classification || "—"}</div>
          <div><span className="text-[var(--jc-text-soft)]">Build:</span> {finding.build_ref || "—"}</div>
          <div><span className="text-[var(--jc-text-soft)]">Session:</span> {finding.session_id || "—"}</div>
        </div>
        <ReadOnlyBlock label="Starting state" value={finding.starting_state} />
        <ReadOnlyBlock label="Exact steps" value={finding.exact_steps} />
        <ReadOnlyBlock label="Expected" value={finding.expected_result} />
        <ReadOnlyBlock label="Actual" value={finding.actual_result} />

        <div>
          <div className="text-xs font-semibold mb-1">Evidence</div>
          {(finding.evidence_attachment_ids || []).length === 0
            && attachments.length === 0
            ? <div className="text-xs text-[var(--jc-text-soft)]">
                No evidence attached yet.
              </div>
            : (
              <ul className="text-xs space-y-1"
                  data-testid="qa-drawer-attachments">
                {(finding.evidence_attachment_ids || []).map((aid) => (
                  <li key={aid} className="flex items-center gap-1">
                    <Paperclip size={12} /> attachment {aid.slice(0, 8)}…
                  </li>
                ))}
              </ul>
            )}
          <label className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold cursor-pointer text-[var(--jc-blue)]">
            <input type="file" className="hidden"
                   data-testid="qa-drawer-evidence-file"
                   onChange={(e) => uploadEvidence(e.target.files?.[0])} />
            <Paperclip size={12} /> Attach evidence
          </label>
        </div>

        <div>
          <div className="text-xs font-semibold mb-1">Event trail</div>
          <ul className="text-xs space-y-1" data-testid="qa-drawer-events">
            {(detail?.events || []).map((e) => (
              <li key={e.id} className="border-l-2 border-[var(--jc-border)] pl-2">
                <span className="font-semibold">{e.event_type}</span>
                <span className="text-[var(--jc-text-soft)]"> · {e.new_status || "—"} · {e.created_at?.slice(0, 16)}</span>
                {e.text && <div className="mt-0.5">{e.text}</div>}
              </li>
            ))}
          </ul>
        </div>

        {finding.engineering_response_text && (
          <div className="jc-card p-3 bg-[var(--jc-bg)]">
            <div className="text-xs font-semibold mb-1">Engineering response</div>
            <div className="text-sm whitespace-pre-wrap">
              {finding.engineering_response_text}
            </div>
          </div>
        )}

        {finding.engineering_status === "ready_for_retest" && (
          <div className="jc-card p-3 space-y-2 border-l-4 border-amber-400"
               data-testid="qa-drawer-retest-block">
            <div className="text-xs font-semibold">Retest — record your outcome</div>
            <textarea rows={3} className="jc-input" value={retestNotes}
                      onChange={(e) => setRetestNotes(e.target.value)}
                      placeholder="Notes on your retest (optional)" />
            <div className="flex items-center gap-2">
              <button data-testid="qa-drawer-retest-pass"
                      disabled={saving}
                      onClick={() => submitRetest("PASS")}
                      className="px-3 py-1.5 rounded jc-btn-blue text-xs font-semibold">
                Retest PASS
              </button>
              <button data-testid="qa-drawer-retest-fail"
                      disabled={saving}
                      onClick={() => submitRetest("FAIL")}
                      className="px-3 py-1.5 rounded bg-red-600 text-white text-xs font-semibold disabled:opacity-50">
                Retest FAIL
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ReadOnlyBlock({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-xs font-semibold text-[var(--jc-text-soft)]">{label}</div>
      <div className="text-sm whitespace-pre-wrap border rounded p-2 bg-[var(--jc-bg)]">
        {value}
      </div>
    </div>
  );
}

export default function QaFindings() {
  const [tab, setTab] = useState("new");
  const [refreshKey, setRefreshKey] = useState(0);
  const [drawerFinding, setDrawerFinding] = useState(null);

  const tabs = useMemo(() => ([
    { id: "new", label: "New Finding", icon: <Bug size={13} /> },
    { id: "mine", label: "My Findings", icon: <ListChecks size={13} /> },
    { id: "retest", label: "Retests Assigned To Me", icon: <RefreshCw size={13} /> },
  ]), []);

  return (
    <DashboardLayout title="QA Findings"
      subtitle="Record and retest your JobClose findings.">
      <div data-testid="qa-findings-page" className="space-y-4">
        <div className="flex items-center gap-1 border-b border-[var(--jc-border)]">
          {tabs.map((t) => (
            <button key={t.id}
                    data-testid={`qa-tab-${t.id}`}
                    onClick={() => setTab(t.id)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border-b-2 -mb-px
                      ${tab === t.id
                        ? "border-[var(--jc-blue)] text-[var(--jc-blue)]"
                        : "border-transparent text-[var(--jc-text-soft)]"}`}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {tab === "new" && (
          <NewFindingTab onSubmitted={(f) => {
            setRefreshKey((k) => k + 1);
            setTab("mine");
            setDrawerFinding(f);
          }} />
        )}
        {tab === "mine" && (
          <FindingsList filter={{}} refreshKey={refreshKey}
                        onOpen={setDrawerFinding} />
        )}
        {tab === "retest" && (
          <FindingsList filter={{ ready_for_retest: true }}
                        refreshKey={refreshKey}
                        onOpen={setDrawerFinding} />
        )}
      </div>

      {drawerFinding && (
        <FindingDrawer finding={drawerFinding}
                        onClose={() => setDrawerFinding(null)}
                        onChange={() => setRefreshKey((k) => k + 1)} />
      )}
    </DashboardLayout>
  );
}
